/**
 * The portal's front door as a lead capture (prospect landing). When the org
 * hint names an office whose prospect pages are published, the same email
 * field also takes new leads, and the page shows that office's privacy notice
 * (`GET /public/tour/:handle/intake`). Anything else: the field only re-sends
 * links to people the office already has, as before.
 */
import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '@/lib/api';

export interface LeadIntake {
  consentVersion: string;
  privacyPolicyUrl: string | null;
  pendingMinutes: number;
}

/** How long the front door waits for the notice before showing the returning-resident form. */
const INTAKE_TIMEOUT_MS = 2500;

/**
 * The office's notice, or null (not published, unknown, no answer in time). A
 * simple GET, no credentials. An old handle answers with the current one: that
 * is followed once. (Without a notice the form sends no consent fields, and the
 * server treats the request as the plain resend to people already on file.)
 */
export async function fetchLeadIntake(handle: string, hops = 1): Promise<LeadIntake | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), INTAKE_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/public/tour/${encodeURIComponent(handle)}/intake`, {
      credentials: 'omit',
      signal: ctrl.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null))?.data;
    if (data && typeof data.redirect === 'string' && hops > 0 && /^[a-z0-9-]{1,64}$/.test(data.redirect)) {
      clearTimeout(timer);
      return fetchLeadIntake(data.redirect, hops - 1);
    }
    if (data && typeof data.consentVersion === 'string') {
      return {
        consentVersion: data.consentVersion,
        privacyPolicyUrl: typeof data.privacyPolicyUrl === 'string' ? data.privacyPolicyUrl : null,
        pendingMinutes: typeof data.pendingMinutes === 'number' ? data.pendingMinutes : 120,
      };
    }
    return null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export type IntakeState = { state: 'loading' } | { state: 'ready'; intake: LeadIntake | null };

/** The notice for this hint; `reloadKey` asks again (after "the notice changed"). */
export function useLeadIntake(hint: string | null, reloadKey = 0): IntakeState {
  const [s, setS] = useState<IntakeState>(hint ? { state: 'loading' } : { state: 'ready', intake: null });
  const shownFor = useRef<string | null>(null);
  useEffect(() => {
    if (!hint) return setS({ state: 'ready', intake: null });
    let live = true;
    // A new hint waits for its answer; asking again for the same one keeps the page as it is meanwhile.
    if (shownFor.current !== hint) setS({ state: 'loading' });
    shownFor.current = hint;
    void fetchLeadIntake(hint).then((intake) => {
      if (live) setS({ state: 'ready', intake });
    });
    return () => {
      live = false;
    };
  }, [hint, reloadKey]);
  return s;
}

// The usual slips in the domain of a typed address, and what was meant.
const DOMAIN_FIXES: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co.il': 'gmail.com',
  'gnail.com': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'yahoo.co': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'walla.com': 'walla.co.il',
  'icloud.co': 'icloud.com',
};

/** "dana@gmial.com" -> "dana@gmail.com"; null when nothing looks mistyped. */
export function suggestEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  const at = email.lastIndexOf('@');
  if (at < 1) return null;
  const fix = DOMAIN_FIXES[email.slice(at + 1)];
  return fix ? `${email.slice(0, at + 1)}${fix}` : null;
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Hebrew joins a one-letter preposition to a Hebrew name, with a hyphen before a Latin one ("ב-Young"). */
export function withPrefix(prefix: 'ב' | 'מ' | 'ל', name: string): string {
  return /^[֐-׿]/.test(name.trim()) ? `${prefix}${name}` : `${prefix}-${name}`;
}
